/**
 * DOM measurement for the Ask Puntt rail's fair-share allocation.
 *
 * Kept separate from the pure `allocateSidebarSections` math so the allocator
 * stays unit-testable without a browser, and so component tests can mock the
 * (un-measurable in jsdom) geometry while still exercising the REAL allocator +
 * slicing wiring.
 */

/** Section vertical padding (`p-2` = 0.5rem). Split between header/view-all chrome. */
const SECTION_PADDING_TOP = 8;
const SECTION_PADDING_BOTTOM = 8;

/** First-paint fallbacks (px) used until real rows are measurable. */
const FALLBACK_HEADER_ROW = 32;
const FALLBACK_VIEW_ALL = 28;
const FALLBACK_ITEM = 44;

const PROJECT_ROW_SELECTOR = '[data-testid="sidebar-ask-puntt-project-item"]';
const CHAT_ROW_SELECTOR = '[data-testid="sidebar-ask-puntt-chat-item"]';
const HEADER_SELECTOR =
  '[data-testid="sidebar-ask-puntt-projects-header"], [data-testid="sidebar-ask-puntt-chats-header"]';
const PROJECTS_VIEW_ALL_SELECTOR =
  '[data-testid="sidebar-ask-puntt-projects-all-link"]';
const CHATS_VIEW_ALL_SELECTOR =
  '[data-testid="sidebar-ask-puntt-chats-all-link"]';
const SHARED_SELECTOR = '[data-testid="sidebar-ask-puntt-shared-section"]';

export interface SidebarRailGeometry {
  /** Pixels available to the Projects + Chats sections combined. */
  availableHeight: number;
  /** Fixed per-section chrome above the list (header + section top padding). */
  headerHeight: number;
  /** Fixed per-section chrome below the list (the "View all" link). */
  viewAllHeight: number;
  /** Measured single-row height (max across project + chat rows). */
  itemHeight: number;
}

function offsetHeightOf(el: Element | null): number {
  const h = el ? (el as HTMLElement).offsetHeight : 0;
  return Number.isFinite(h) && h > 0 ? h : 0;
}

function maxOffsetHeight(root: ParentNode, selector: string): number {
  let max = 0;
  for (const el of Array.from(root.querySelectorAll(selector))) {
    max = Math.max(max, offsetHeightOf(el));
  }
  return max;
}

/**
 * Measure the geometry the allocator needs from the live rail DOM.
 *
 * `availableHeight` is the bounded content container's height minus the height
 * of every NON-Ask-Puntt sibling (top nav, the Documents button, the folder
 * tree) and minus the optional "Shared with me" section — i.e. exactly the
 * room left for the two stacked Projects/Chats sections in BOTH layout modes
 * (Documents-hidden fill-rail and the static Documents-coexist mode).
 *
 * Returns `null` when the container is not laid out yet (height 0, e.g. jsdom
 * or first paint) so the caller keeps row counts at zero until real geometry
 * exists.
 */
export function measureAskPunttRailGeometry(
  slot: HTMLElement,
  fallbackItemHeight: number,
): SidebarRailGeometry | null {
  const container = slot.parentElement;
  if (!container) return null;

  const containerHeight = container.clientHeight;
  if (!Number.isFinite(containerHeight) || containerHeight <= 0) return null;

  let othersHeight = 0;
  for (const child of Array.from(container.children)) {
    if (child !== slot) othersHeight += offsetHeightOf(child);
  }

  const sharedHeight = offsetHeightOf(slot.querySelector(SHARED_SELECTOR));

  const projectRowHeight = maxOffsetHeight(slot, PROJECT_ROW_SELECTOR);
  const chatRowHeight = maxOffsetHeight(slot, CHAT_ROW_SELECTOR);
  const itemHeight = Math.max(
    projectRowHeight,
    chatRowHeight,
    fallbackItemHeight,
    FALLBACK_ITEM,
  );

  const headerRowHeight =
    offsetHeightOf(slot.querySelector(HEADER_SELECTOR)) || FALLBACK_HEADER_ROW;
  const headerHeight = headerRowHeight + SECTION_PADDING_TOP;

  const viewAllRowHeight = Math.max(
    offsetHeightOf(slot.querySelector(PROJECTS_VIEW_ALL_SELECTOR)),
    offsetHeightOf(slot.querySelector(CHATS_VIEW_ALL_SELECTOR)),
    FALLBACK_VIEW_ALL,
  );
  const viewAllHeight = viewAllRowHeight + SECTION_PADDING_BOTTOM;

  return {
    availableHeight: containerHeight - othersHeight - sharedHeight,
    headerHeight,
    viewAllHeight,
    itemHeight,
  };
}
